// HTML 報價單產生工具
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { TemplateManager, QuoteTemplateData } from './templateManager';
import { calculateLineAmounts, getQuoteLineDisplay, isMarkupRateUnit, summarizeQuoteItemTotals } from '../utils/quoteItemAmounts';
import {
  QUOTE_PDF_UNBREAKABLE_SELECTORS,
  collectElementRanges,
  computePageSlices,
} from '../utils/pdfPageSlices';

// 型別定義
export interface QuoteItem {
  item_name: string;
  service_type: string;
  list_price: number;
  contract_price: number;
  unit?: string;
  estimated_hours?: number;
  quantity?: number;
  subtotal: number;
  discount_amount: number;
  discounted_total: number;
}

export interface QuoteData {
  quote_number: string;
  project_name: string;
  company_name?: string;
  company_tax_id?: string;
  company_address?: string;
  customer_name: string;
  customer_tax_id?: string;
  contact_name?: string;
  contact_email?: string;
  creator_name?: string;
  creator_email?: string;
  issue_date: string;
  valid_until: string;
  items: QuoteItem[];
  subtotal: number;
  total_discount: number;
  discounted_total: number;
  // 新增的自動計算欄位
  subtotal_amount?: number; // 小計金額（sum of quote_items.subtotal）
  subtotal_discount?: number; // 折扣金額（sum of quote_items.subtotal_discount）
  subtotal_afterdiscount?: number; // 折扣後小計金額（sum of quote_items.subtotal_afterdiscount）
  tax_amount: number;
  total_amount: number;
  terms?: string;
  serviceRates?: Array<{
    service_type: string;
    unit_price: number;
    unit: string;
    year?: number;
  }>;
}

// 格式化日期：僅顯示年-月-日
function formatDate(dateString: string | null | undefined): string {
  if (!dateString) {
    return '';
  }
  try {
    // 如果已經是 YYYY-MM-DD 格式，直接返回
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    // 嘗試解析日期並格式化為 YYYY-MM-DD
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString; // 如果無法解析，返回原始值
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    return dateString; // 如果出錯，返回原始值
  }
}

// 格式化數量：如果是整數則顯示整數，否則顯示小數，包含千分位符號
function formatQuantity(value: number): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }
  const numValue = Number(value);
  // 檢查是否為整數（考慮浮點數精度問題）
  if (Number.isInteger(numValue) || Math.abs(numValue - Math.round(numValue)) < 0.0001) {
    // 整數：不顯示小數點，但包含千分位符號
    return numValue.toLocaleString('zh-TW', { maximumFractionDigits: 0 });
  }
  // 小數：顯示小數，包含千分位符號
  return numValue.toLocaleString('zh-TW', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

// 格式化數字：整數顯示整數（無小數點），小數顯示小數，所有數字包含千分位符號
function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }
  const numValue = Number(value);
  // 檢查是否為整數（考慮浮點數精度問題）
  if (Number.isInteger(numValue) || Math.abs(numValue - Math.round(numValue)) < 0.0001) {
    // 整數：不顯示小數點，但包含千分位符號
    return numValue.toLocaleString('zh-TW', { maximumFractionDigits: 0 });
  }
  // 小數：顯示小數，包含千分位符號
  return numValue.toLocaleString('zh-TW', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

// 格式化金額：四捨五入為整數，僅顯示為帶有千分位符號的整數
function formatRoundedAmount(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }
  const numValue = Number(value);
  const roundedValue = Math.round(numValue);
  return roundedValue.toLocaleString('zh-TW', { maximumFractionDigits: 0 });
}

// 計算服務項目類別摘要
function generateServiceTypeSummary(items: QuoteItem[]): Array<{
  service_type: string;
  total_hours: number;
  total_amount: number;
  unit: string;
}> {
  const summary = new Map<string, { total_hours: number; total_amount: number; unit: string }>();
  
  items.forEach(item => {
    const serviceType = item.service_type || '未分類';
    // 計算該項目的工時/數量：優先使用 quantity，如果沒有則使用 estimated_hours
    // 確保轉換為數字類型，避免字符串拼接
    const storedQty = Number(item.quantity ?? item.estimated_hours ?? 0) || 0;
    const display = getQuoteLineDisplay({
      listPrice: Number(item.list_price) || 0,
      contractPrice: Number(item.contract_price) || 0,
      quantity: storedQty,
      unit: item.unit,
      forceMarkup: isMarkupRateUnit(item.unit)
    });
    const quantity = display.quantityDisplay;
    const amount = Number(item.discounted_total) || 0;
    const unit = display.quantityUnitDisplay;
    
    if (summary.has(serviceType)) {
      const existing = summary.get(serviceType)!;
      // 累加該類別下所有項目的工時/數量總計（確保是數字運算）
      existing.total_hours = Number(existing.total_hours) + quantity;
      existing.total_amount = Number(existing.total_amount) + amount;
    } else {
      // 初始化該類別的總計
      summary.set(serviceType, { 
        total_hours: quantity, 
        total_amount: amount, 
        unit 
      });
    }
  });
  
  return Array.from(summary.entries()).map(([service_type, data]) => ({
    service_type,
    total_hours: Number(data.total_hours) || 0, // 該類別的工時/數量總計
    total_amount: Number(data.total_amount) || 0,
    unit: data.unit
  }));
}

// 取得當年度服務收費表的單價
function getCurrentYearListPrice(
  serviceType: string,
  serviceRates?: Array<{ service_type: string; unit_price: number; unit: string; year?: number }>,
  fallbackPrice?: number
): number {
  if (!serviceRates || serviceRates.length === 0) {
    return fallbackPrice ?? 0;
  }

  const currentYear = new Date().getFullYear();
  
  // 優先查找當年度服務收費
  const currentYearRate = serviceRates.find(
    rate => rate.service_type === serviceType && rate.year === currentYear
  );
  
  if (currentYearRate) {
    return currentYearRate.unit_price;
  }

  // 如果找不到當年度，查找最新年度的服務收費
  const matchingRates = serviceRates.filter(rate => rate.service_type === serviceType);
  if (matchingRates.length > 0) {
    // 按年度降序排序，取最新的
    const sortedRates = matchingRates.sort((a, b) => (b.year ?? 0) - (a.year ?? 0));
    return sortedRates[0].unit_price;
  }

  // 如果都找不到，使用備用價格（原本儲存的 list_price）
  return fallbackPrice ?? 0;
}

// 產生報價單 HTML 字串
export async function generateQuoteHtml(quote: QuoteData, stampBase64?: string): Promise<string> {
  // 計算服務項目類別摘要
  const serviceTypeSummary = generateServiceTypeSummary(quote.items);
  
  // 服務項目類別摘要表格
  const summaryHtml = serviceTypeSummary.map(summary => `
    <tr>
      <td class="left">${summary.service_type}</td>
      <td class="center">${formatQuantity(summary.total_hours)}（${summary.unit}）</td>
      <td class="right">NT$ ${formatRoundedAmount(summary.total_amount)}</td>
    </tr>
  `).join('');
  
  // 明細表格 - 使用當年度服務收費表的單價作為牌價
  const detailsHtml = quote.items.map((item: QuoteItem) => {
    const asMarkup = isMarkupRateUnit(item.unit);
    const listPrice = asMarkup
      ? (item.contract_price ?? 0)
      : (item.list_price ?? getCurrentYearListPrice(
        item.service_type,
        quote.serviceRates,
        item.list_price
      ));
    const qty = item.quantity ?? item.estimated_hours ?? 0;
    const amounts = calculateLineAmounts({
      listPrice,
      contractPrice: item.contract_price,
      quantity: qty,
      unit: item.unit,
      forceMarkup: asMarkup
    });
    const display = getQuoteLineDisplay({
      listPrice,
      contractPrice: item.contract_price,
      quantity: qty,
      unit: item.unit,
      forceMarkup: asMarkup
    });
    const listPriceLabel = display.isMarkup
      ? '—'
      : formatNumber(listPrice);
    const contractPriceLabel = display.isMarkup
      ? formatRoundedAmount(display.contractPriceAmount)
      : formatNumber(item.contract_price);
    const discountAmount = display.isMarkup
      ? 0
      : (item.discount_amount ?? amounts.subtotal_discount);
    const discountedTotal = item.discounted_total ?? amounts.subtotal_afterdiscount;
    
    return `
    <tr>
      <td class="left">${item.item_name}</td>
      <td>${item.service_type || ''}</td>
      <td>${listPriceLabel}</td>
      <td>${contractPriceLabel}</td>
      <td class="quantity-col">${formatQuantity(display.quantityDisplay)}</td>
      <td>${formatRoundedAmount(discountAmount)}</td>
      <td class="final-col">${formatRoundedAmount(discountedTotal)}</td>
    </tr>
  `;
  }).join('');

  // 準備模板資料
  const amountSummary = summarizeQuoteItemTotals(quote.items);
  const templateData: QuoteTemplateData = {
    PROJECT_NAME: quote.project_name || '',
    QUOTE_NUMBER: quote.quote_number || '',
    VALID_UNTIL: formatDate(quote.valid_until),
    CUSTOMER_NAME: quote.customer_name || '',
    CUSTOMER_TAX_ID: quote.customer_tax_id || '',
    CONTACT_NAME: quote.contact_name || '',
    CONTACT_EMAIL: quote.contact_email || '',
    CREATOR_NAME: quote.creator_name || '',
    CREATOR_EMAIL: quote.creator_email || '',
    SUMMARY_ROWS: summaryHtml,
    DETAIL_ROWS: detailsHtml,
    SUBTOTAL: formatRoundedAmount(amountSummary.subtotal),
    TOTAL_DISCOUNT: formatRoundedAmount(amountSummary.discount),
    HOURS_TOTAL: formatRoundedAmount(amountSummary.hoursAfterDiscount),
    PROCUREMENT_TOTAL: formatRoundedAmount(amountSummary.procurementAfterDiscount),
    DISCOUNTED_TOTAL: formatRoundedAmount(amountSummary.afterDiscount),
    TAX_AMOUNT: formatRoundedAmount(amountSummary.tax),
    TOTAL_AMOUNT: formatRoundedAmount(amountSummary.total),
    TERMS: quote.terms || '',
    STAMP_BASE64: stampBase64 || '',
    SIGN_DATE: new Date().toLocaleDateString('zh-TW')
  };

  // 載入並渲染模板
  const template = await TemplateManager.loadTemplate('quote');
  const html = TemplateManager.renderTemplate(template, templateData);

  return html;
}

// 添加頁首頁尾函數
function addPageHeaderFooter(pdf: jsPDF, pageNumber: number, totalPages: number, quote: QuoteData) {
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  
  // 設定字體和顏色
  pdf.setFontSize(8);
  pdf.setTextColor(153, 153, 153); // #999
  
  // 添加頁首文字
  const headerText = `Teleworker Inc. - ${quote.quote_number}`;
  pdf.text(headerText, pdfWidth / 2, 15, { align: 'center' });
  
  // 添加頁尾文字（提高位置，避免與內容重疊）
  const footerText = `Page ${pageNumber}/${totalPages}`;
  pdf.text(footerText, pdfWidth / 2, pdfHeight - 8, { align: 'center' });
  
  // 添加分隔線
  pdf.setDrawColor(238, 238, 238); // #eee
  pdf.line(10, 20, pdfWidth - 10, 20); // 頁首分隔線
  pdf.line(10, pdfHeight - 20, pdfWidth - 10, pdfHeight - 20); // 頁尾分隔線（提高位置）
}

// 分頁生成 PDF 函數
async function generatePdfByPages(pdf: jsPDF, tempDiv: HTMLElement, quote: QuoteData) {
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  
  // 將整個內容作為一個元素處理
  let cloneHeight = 0;
  let cloneRanges = collectElementRanges(tempDiv, QUOTE_PDF_UNBREAKABLE_SELECTORS, 1);

  const canvas = await html2canvas(tempDiv, {
    scale: 1, // 降低解析度以減少檔案大小
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    width: 800,
    height: tempDiv.scrollHeight,
    scrollX: 0,
    scrollY: 0,
    imageTimeout: 0, // 避免圖片載入超時
    logging: false, // 關閉日誌以提升效能
    onclone: (_clonedDoc, clonedElement) => {
      if (!clonedElement) {
        return;
      }
      cloneHeight = clonedElement.scrollHeight;
      cloneRanges = collectElementRanges(clonedElement, QUOTE_PDF_UNBREAKABLE_SELECTORS, 1);
    }
  });

  const availableHeightMm = pdfHeight - 45; // 留 20mm 給頁首，25mm 給頁尾
  const imgWidth = pdfWidth - 20;
  const canvasWidth = Math.max(canvas.width, 1);
  const pageHeightCanvas = (availableHeightMm * canvasWidth) / imgWidth * 0.98;
  const scaleY = cloneHeight > 0 ? canvas.height / cloneHeight : (tempDiv.scrollHeight > 0 ? canvas.height / tempDiv.scrollHeight : 1);
  const unbreakableRanges = cloneRanges.map((range) => ({
    ...range,
    top: range.top * scaleY,
    bottom: range.bottom * scaleY
  }));
  const slices = computePageSlices(canvas.height, pageHeightCanvas, unbreakableRanges);
  const totalPages = Math.max(slices.length, 1);

  slices.forEach((slice, index) => {
    const pageNumber = index + 1;
    addPageHeaderFooter(pdf, pageNumber, totalPages, quote);

    const sourceY = Math.max(0, Math.floor(slice.start));
    const sourceEnd = Math.min(canvas.height, Math.floor(slice.end));
    const sourceHeight = Math.max(1, sourceEnd - sourceY);

    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    tempCanvas.width = canvas.width;
    tempCanvas.height = sourceHeight;

    if (tempCtx && sourceY < canvas.height) {
      tempCtx.drawImage(
        canvas,
        0,
        sourceY,
        canvas.width,
        sourceHeight,
        0,
        0,
        canvas.width,
        sourceHeight
      );
      const tempImgData = tempCanvas.toDataURL('image/jpeg', 0.8);
      const tempImgHeight = (sourceHeight * imgWidth) / canvasWidth;
      pdf.addImage(tempImgData, 'JPEG', 10, 20, imgWidth, tempImgHeight);
    }

    if (pageNumber < totalPages) {
      pdf.addPage();
    }
  });
}

// 生成 PDF Blob
export async function generateQuotePdfBlob(quote: QuoteData, stampBase64?: string): Promise<Blob> {
  const html = await generateQuoteHtml(quote, stampBase64);
  
  try {
    // 創建一個臨時的 div 元素來放置 HTML 內容
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.top = '0';
    tempDiv.style.width = '800px'; // 設定固定寬度
    document.body.appendChild(tempDiv);
    
    // 使用 jsPDF 生成 PDF，添加壓縮選項
    const pdf = new jsPDF({
      orientation: 'p',
      unit: 'mm',
      format: 'a4',
      compress: true // 啟用壓縮
    });
    
    // 分頁生成 PDF
    await generatePdfByPages(pdf, tempDiv, quote);
    
    // 移除臨時元素
    document.body.removeChild(tempDiv);
    
    // 返回 PDF blob
    return pdf.output('blob');
    
  } catch (error) {
    // 如果客戶端生成失敗，嘗試使用伺服器端生成
    try {
      return await generateQuotePdfBlobServer(quote, stampBase64);
    } catch {
      throw new Error(`PDF 產生失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
    }
  }
}

// 下載 PDF
export async function downloadQuotePdf(quote: QuoteData, fileName = '報價單.pdf', stampBase64?: string) {
  try {
    const blob = await generateQuotePdfBlob(quote, stampBase64);
    saveAs(blob, fileName);
  } catch (error) {
    throw new Error(`PDF 下載失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
  }
}

// 伺服器端 PDF 生成 Blob（作為備用方案）
async function generateQuotePdfBlobServer(quote: QuoteData, stampBase64?: string): Promise<Blob> {
  const html = await generateQuoteHtml(quote, stampBase64);
  
  // 動態檢測 API 端點
  const apiUrl = process.env.REACT_APP_API_URL || 'https://teleworker-api-5ubu67iiga-de.a.run.app';
  
  const res = await fetch(`${apiUrl}/html-to-pdf`, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'Accept': 'application/pdf'
    },
    body: JSON.stringify({ 
      html, 
      fileName: `報價單_${quote.quote_number}.pdf`,
      project_name: quote.project_name,
      quote_number: quote.quote_number,
      valid_until: quote.valid_until
    })
  });
  
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`PDF 產生失敗: ${res.status} - ${errorText}`);
  }
  
  return await res.blob();
}

 